<template>
  <div
    class="asset-relationship-graph-container relative flex flex-1 min-h-0"
    style="direction: ltr"
  >
    <div ref="cyContainer" :style="{ height, width: '100%' }" />
  </div>
</template>

<script>
import Vue from 'vue'
import buildGraph from './graph-builder'
import GraphNode from './graph-node'

export default {
  name: 'RelationshipGraph',
  props: {
    fullscreen: {
      type: Boolean,
      default: false,
    },
    relations: {
      type: Array,
      default() {
        return []
      },
    },
    source: {
      type: Object,
      required: true,
    },
    moduleName: { type: String, required: true },
  },
  data() {
    return {
      elements: [],
      height: null,
      selectedId: `node-${this.source.id}-${this.source.model}`,
    }
  },
  watch: {
    relations() {
      this.buildElements()
      this.rebuildGraph()
    },
    fullscreen(newValue) {
      this.rebuildGraph()
      if (newValue) {
        this.cy.fit()
      }
      this.cy.userZoomingEnabled(newValue)
    },
  },
  created() {
    this.buildElements(true)
  },
  mounted() {
    this.height = getComputedStyle(this.$el).height
    this.$nextTick(() => {
      this.makeGraph()
    })
  },
  beforeDestroy() {
    this.destroyChart()
  },
  methods: {
    rebuildGraph() {
      this.height = getComputedStyle(this.$el).height
      if (this.cy) {
        const selectedNode = this.cy.nodes(':selected').first().data()
        this.selectedId = (selectedNode || {}).id
      }
      this.destroyChart()
      this.makeGraph()
    },
    makeGraph() {
      this.cy = buildGraph(this.$refs.cyContainer, this.elements)
      const cy = this.cy
      cy.userZoomingEnabled(false)
      this.cy.nodeHtmlLabel([
        {
          query: 'node', // cytoscape query selector
          halign: 'center', // title vertical position. Can be 'left',''center, 'right'
          valign: 'center', // title vertical position. Can be 'top',''center, 'bottom'
          halignBox: 'center', // title vertical position. Can be 'left',''center, 'right'
          valignBox: 'center', // title relative box vertical position. Can be 'top',''center, 'bottom'
          cssClass: '', // any classes will be as attribute of <div> container for every title
          tpl(data) {
            const selectedNode = cy.nodes(':selected').first().data()
            const Node = Vue.extend(GraphNode)
            return new Node({
              propsData: {
                node: data,
                selected: selectedNode && selectedNode.id === data.id,
              },
            }).$mount().$el.outerHTML
          },
        },
      ])
      this.cy.center()
      this.cy.nodes().on('click', (e) => {
        const element = e.target
        this.selectedId = element.id()
        this.$emit('click', element.data())
      })
    },
    buildElements() {
      const source = this.source
      const data = [
        {
          id: source.id,
          resourceId: source.id,
          name:
            this.moduleName === this.$constants.CMDB
              ? source.ciName
              : source.name,
          model: source.model,
          assetIconName: source.assetIconName,
          displayName:
            this.moduleName === this.$constants.CMDB
              ? source.ciDisplayName
              : source.displayName,
          assetTypeId:
            this.moduleName === this.$constants.CMDB
              ? source.ciTypeId
              : source.assetTypeId,
          children: this.relations,
        },
      ]
      const reduce = (prevResult, node) => {
        const prevResults = [...prevResult]
        const current = [
          this.buildNode(node),
          ...(node.children || []).map(this.buildNode),
          ...(node.children || []).map((relation) =>
            this.buildEdge(node, relation)
          ),
          ...(node.children || []).reduce(reduce, []),
        ]
        return [...prevResults, ...current]
      }
      this.elements = data.reduce(reduce, [])
    },
    buildNode(relation) {
      const id = `node-${relation.resourceId}-${relation.model}`
      return {
        // grabbable: false,
        group: 'nodes',
        width: 'label',
        height: 'label',
        ...(id === this.selectedId ? { selected: true } : {}),
        data: {
          ...relation,
          label: relation.name,
          children: undefined,
          id,
        },
      }
    },
    buildEdge(source, relation) {
      return {
        group: 'edges',
        data: {
          ...relation,
          children: undefined,
          label: `${relation.relation}`,
          id: `edge-${relation.id}`,
          ...(relation.arrowInward
            ? {
                target: `node-${source.resourceId}-${source.model}`,
                source: `node-${relation.resourceId}-${relation.model}`,
              }
            : {
                source: `node-${source.resourceId}-${source.model}`,
                target: `node-${relation.resourceId}-${relation.model}`,
              }),
        },
        pannable: false,
      }
    },
    destroyChart() {
      if (this.cy) {
        this.cy.panzoom('destroy')
        this.cy.destroy()
        this.cy = null
      }
    },
    print() {
      if (this.cy) {
        // const png = this.cy.png({
        //   bg: 'white',
        //   full: true,
        //   maxWidth: 800,
        //   maxHeight: 800,
        // })
        // console.log(png)
        // import('jspdf').then((Pdf) => {
        //   Pdf = Pdf.default || Pdf
        //   const doc = new Pdf('p', 'in', 'a4')
        //   doc.autoPrint()
        //   doc.addImage(png, 'png', 12, 12)
        //   doc.save(`${this.source.name}-relationships.pdf`)
        // })
      }
    },
  },
}
</script>

<style lang="less">
@import '~cytoscape-panzoom/cytoscape.js-panzoom.css';

.cy-panzoom {
  top: 10px;
  right: 60px;
  z-index: 2;

  .fa.fa-expand {
    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      content: ' ';
      background: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fal' data-icon='building' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' class='svg-inline--fa fa-expand fa-w-14 fa-lg'%3E%3Cpath fill='%237b8fa5' d='M0 180V56c0-13.3 10.7-24 24-24h124c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12H64v84c0 6.6-5.4 12-12 12H12c-6.6 0-12-5.4-12-12zM288 44v40c0 6.6 5.4 12 12 12h84v84c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12V56c0-13.3-10.7-24-24-24H300c-6.6 0-12 5.4-12 12zm148 276h-40c-6.6 0-12 5.4-12 12v84h-84c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h124c13.3 0 24-10.7 24-24V332c0-6.6-5.4-12-12-12zM160 468v-40c0-6.6-5.4-12-12-12H64v-84c0-6.6-5.4-12-12-12H12c-6.6 0-12 5.4-12 12v124c0 13.3 10.7 24 24 24h124c6.6 0 12-5.4 12-12z' class=''%3E%3C/path%3E%3C/svg%3E")
        center center no-repeat;
    }
  }

  .fa.fa-plus {
    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      content: ' ';
      background: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fal' data-icon='building' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' class='svg-inline--fa fa-expand fa-w-14 fa-lg'%3E%3Cpath fill='%237b8fa5' d='M416 208H272V64c0-17.67-14.33-32-32-32h-32c-17.67 0-32 14.33-32 32v144H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h144v144c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32V304h144c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z' class=''%3E%3C/path%3E%3C/svg%3E")
        center center no-repeat;
    }
  }

  .fa.fa-minus {
    &::before {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      content: ' ';
      background: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fal' data-icon='building' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' class='svg-inline--fa fa-expand fa-w-14 fa-lg'%3E%3Cpath fill='%237b8fa5' d='M416 208H32c-17.67 0-32 14.33-32 32v32c0 17.67 14.33 32 32 32h384c17.67 0 32-14.33 32-32v-32c0-17.67-14.33-32-32-32z' class=''%3E%3C/path%3E%3C/svg%3E")
        center center no-repeat;
    }
  }
}

.asset-relationship-graph-container {
  .asset-relationship-node-box {
    @apply rounded p-3 bg-white;

    height: 46px !important;
    color: var(--primary);
    cursor: pointer;
    border: 1px solid var(--primary);

    &.selected-node {
      @apply bg-primary text-white;

      border-color: var(--primary);

      .text-neutral-light {
        color: white;

        svg {
          fill: currentcolor;

          path {
            fill: currentcolor;
          }
        }
      }
    }
  }
}
</style>
